// Public page: recurrency follow-up form. User enters email, fills form, picks a time slot.
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { firestore, functions } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getActiveEventId } from '../utils/activeEvent';
import './RecurrencyUpdate.css';

const getFlyerForRecurrencyUpdateFn = httpsCallable(functions, 'getFlyerForRecurrencyUpdate');
const getRecurrencySlotCountsFn = httpsCallable(functions, 'getRecurrencySlotCounts');

const STEPS = { EMAIL: 1, FORM: 2, SLOT: 3, DONE: 4 };

const RecurrencyUpdate = () => {
  const [searchParams] = useSearchParams();
  const eventIdFromUrl = searchParams.get('event');
  const [activeEvent, setActiveEvent] = useState(null);
  const [step, setStep] = useState(STEPS.EMAIL);
  const [email, setEmail] = useState('');
  const [flyer, setFlyer] = useState(null);
  const [recurrency, setRecurrency] = useState('');
  const [lastJumpDate, setLastJumpDate] = useState('');
  const [availableWednesdayMorning, setAvailableWednesdayMorning] = useState('');
  const [notes, setNotes] = useState('');
  const [timeSlotId, setTimeSlotId] = useState('');
  const [slotCounts, setSlotCounts] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function fetchEvent() {
      try {
        if (eventIdFromUrl) {
          const eventSnap = await getDoc(doc(firestore, 'events', eventIdFromUrl));
          if (eventSnap.exists()) {
            setActiveEvent({ id: eventSnap.id, ...eventSnap.data() });
          } else {
            setError('Event not found.');
          }
          return;
        }
        const id = await getActiveEventId();
        if (!id) {
          setError('No active event configured.');
          return;
        }
        const eventSnap = await getDoc(doc(firestore, 'events', id));
        if (eventSnap.exists()) {
          setActiveEvent({ id: eventSnap.id, ...eventSnap.data() });
        } else {
          setError('No active event configured.');
        }
      } catch (err) {
        setError(err.message || 'Error fetching event.');
      }
    }
    fetchEvent();
  }, [eventIdFromUrl]);

  useEffect(() => {
    if (step !== STEPS.SLOT || !activeEvent?.id) return;
    async function fetchSlotCounts() {
      try {
        const result = await getRecurrencySlotCountsFn({eventId: activeEvent.id});
        setSlotCounts(result.data.counts || {});
      } catch (err) {
        console.error('Error fetching slot counts:', err);
      }
    }
    fetchSlotCounts();
  }, [step, activeEvent?.id]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (!activeEvent) {
        setError('No active event available.');
        setLoading(false);
        return;
      }
      const emailTrim = (email || '').trim().toLowerCase();
      if (!emailTrim) {
        setError('Please enter your email.');
        setLoading(false);
        return;
      }
      const result = await getFlyerForRecurrencyUpdateFn({eventId: activeEvent.id, email: emailTrim});
      const found = result.data;
      if (!found || !found.found) {
        setError('No registration found for this email. Please use the email you registered with.');
        setLoading(false);
        return;
      }
      setFlyer(found);
      setRecurrency(found.recurrency || 'Yes');
      setLastJumpDate(found.recurrencyUpdate?.lastJumpDate || '');
      setAvailableWednesdayMorning(found.recurrencyUpdate?.availableWednesdayMorning || '');
      setNotes(found.recurrencyUpdate?.notes || '');
      setStep(STEPS.FORM);
    } catch (err) {
      setError(err.message || 'Lookup failed.');
    }
    setLoading(false);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setError('');
    const slots = (activeEvent?.recurrencyTimeSlots || []);
    if (recurrency === 'No') {
      handleFinalSubmit(null);
      return;
    }
    if (slots.length === 0) {
      handleFinalSubmit(null);
      return;
    }
    setStep(STEPS.SLOT);
  };

  const handleFinalSubmit = async (chosenSlotId) => {
    const slotId = chosenSlotId !== undefined ? chosenSlotId : timeSlotId;
    setError('');
    setLoading(true);
    try {
      const submitRecurrencyUpdate = httpsCallable(functions, 'submitRecurrencyUpdate');
      await submitRecurrencyUpdate({
        eventId: activeEvent.id,
        email: (email || '').trim(),
        recurrency: (recurrency || '').trim() || null,
        lastJumpDate: (lastJumpDate || '').trim() || null,
        availableWednesdayMorning: (availableWednesdayMorning || '').trim() || null,
        notes: (notes || '').trim() || null,
        timeSlotId: slotId == null || slotId === '' ? null : String(slotId).trim(),
      });
      setStep(STEPS.DONE);
    } catch (err) {
      setError(err.message || (err.data?.message) || 'Submission failed.');
    }
    setLoading(false);
  };

  const slots = (activeEvent?.recurrencyTimeSlots || []);
  const isSlotFull = (slot) => {
    const max = Math.max(0, Number(slot.maxParticipants) || 0);
    if (max === 0) return false;
    const count = slotCounts[slot.id] || 0;
    return count >= max;
  };

  if (!activeEvent && !error) {
    return <div className="recurrency-update-page"><p>Loading…</p></div>;
  }

  if (step === STEPS.DONE) {
    return (
      <div className="recurrency-update-page">
        <h2>Thank you</h2>
        <p>Your recurrency update has been saved. We&apos;ll use this when planning the event.</p>
      </div>
    );
  }

  return (
    <div className="recurrency-update-page">
      <h2>Recurrency update</h2>
      <p className="recurrency-update-subtitle">You can come back here anytime to update your status if you manage to get a jump in!</p>
      {error && <p className="recurrency-update-error">{error}</p>}

      {step === STEPS.EMAIL && (
        <form onSubmit={handleEmailSubmit} className="recurrency-update-form">
          <p>Enter the email address you used to register.</p>
          <label htmlFor="ru-email">Email:</label>
          <input
            id="ru-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <button type="submit" disabled={loading}>{loading ? 'Checking…' : 'Continue'}</button>
        </form>
      )}

      {step === STEPS.FORM && (
        <form onSubmit={handleFormSubmit} className="recurrency-update-form">
          <p>Hello{flyer?.name ? `, ${flyer.name}` : ''}. Please answer the following.</p>
          <p>
            If you do not need a recurrency with us (you jumped, or you scheduled a recurrency with the school),
            select No below and hit submit.
          </p>
          <div className="form-group">
            <label>Do you still need a refresher?</label>
            <select
              value={recurrency}
              onChange={(e) => setRecurrency(e.target.value)}
              required
            >
              <option value="">— Select —</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
              <option value="Unsure">Unsure</option>
            </select>
          </div>
          {recurrency !== 'No' && (
            <>
              <div className="form-group">
                <label>Date of your last jump:</label>
                <input
                  type="date"
                  value={lastJumpDate}
                  onChange={(e) => setLastJumpDate(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Will you be available on Wednesday morning?</label>
                <select
                  value={availableWednesdayMorning}
                  onChange={(e) => setAvailableWednesdayMorning(e.target.value)}
                >
                  <option value="">— Select —</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
                {availableWednesdayMorning === 'No' && (
                  <p className="recurrency-update-wed-message">
                    Our free recurrency program happens Wednesday morning. If you need a recurrency at a different day or time, please contact the Perris School at 951-888-5757. You can still submit this form if you would like to let us know your status.
                  </p>
                )}
              </div>
              <div className="form-group">
                <label>Anything we should know about your recurrency situation?</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Optional"
                />
              </div>
            </>
          )}
          <button type="submit">{recurrency === 'No' ? 'Submit' : 'Continue to time slot'}</button>
        </form>
      )}

      {step === STEPS.SLOT && (
        <div className="recurrency-update-form">
          <p>Choose an available time slot (or skip if none are configured).</p>
          {slots.length === 0 ? (
            <p className="recurrency-update-muted">No time slots configured. Submit your answers without a slot.</p>
          ) : (
            <ul className="recurrency-update-slots">
              {slots.map((slot) => {
                const full = isSlotFull(slot);
                const count = slotCounts[slot.id] || 0;
                const max = Math.max(0, Number(slot.maxParticipants) || 0);
                const spotsText = max > 0 ? ` (${count}/${max} spots)` : '';
                const slotLabel = slot.checkIn && slot.start
                  ? `Check in ${slot.checkIn}, ${slot.start} class begins`
                  : slot.start
                    ? `${slot.start} class begins`
                    : (slot.label || slot.id);
                return (
                  <li key={slot.id}>
                    <label className={full ? 'recurrency-update-slot-full' : ''}>
                      <input
                        type="radio"
                        name="timeSlot"
                        value={slot.id}
                        checked={timeSlotId === slot.id}
                        onChange={() => setTimeSlotId(slot.id)}
                        disabled={full}
                      />
                      {slotLabel}{spotsText}
                      {full && ' — Full'}
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
          <button
            type="button"
            onClick={() => handleFinalSubmit(slots.length > 0 ? timeSlotId || null : null)}
            disabled={loading || (slots.length > 0 && !timeSlotId && !slots.every(isSlotFull))}
          >
            {loading ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      )}
    </div>
  );
};

export default RecurrencyUpdate;
